/**
 * Levels / XP / win streak (K9). Pure, shared by server (awards) and client (level ring).
 *
 * - XP: every settled round gives floor(stake / tokensPerXp) XP. Stakes below `tokensPerXp` and zero-stake
 *   rounds (free spins inside a feature) give nothing: a minimum of 1 XP per round made 1-token bets level up
 *   10× faster per token than any other stake, and with level rewards that becomes a token farm.
 * - Level curve: going from level L to L+1 needs `base + step × (L − 1)` XP (100, 150, 200, …).
 *   User.xp stores the TOTAL XP; the level is derived from it (and cached in User.level).
 * - Win streak ×1–×5 (design: Motion "Win streak"): consecutive rounds that paid MORE than the stake.
 *   A losing round resets it, a push (payout = stake) keeps it. Purely cosmetic: no rewards, no penalty,
 *   nothing asks the player to come back or keep playing (honest-play: no pressure).
 * - Level rewards (owner decision №8, 30.09): reaching level L credits `levelReward(L)` tokens through the
 *   ledger (`level_reward`, key `level:<L>` → exactly once per user and level). Lucky amounts, no digit 4.
 *   Economy: `docs/owner-decisions.md` №8.
 */
export const PROGRESSION = {
  tokensPerXp: 10,
  minXpPerRound: 0,
  base: 100,
  step: 50,
  maxLevel: 200,
  streakMax: 5,
} as const;

export function xpForRound(stake: number): number {
  if (!(stake > 0)) return 0;
  return Math.max(PROGRESSION.minXpPerRound, Math.floor(stake / PROGRESSION.tokensPerXp));
}

/** Tokens for reaching a level: `perLevel`, every `milestoneEvery`-th level `milestone` instead. */
export const LEVEL_REWARDS = { perLevel: 88, milestone: 888, milestoneEvery: 10 } as const;

export function levelReward(level: number): number {
  if (!Number.isInteger(level) || level < 2 || level > PROGRESSION.maxLevel) return 0;
  return level % LEVEL_REWARDS.milestoneEvery === 0 ? LEVEL_REWARDS.milestone : LEVEL_REWARDS.perLevel;
}

/** Rewards for every level in (from, to] — what a level-up from `from` to `to` pays. */
export function levelRewardsBetween(from: number, to: number): Array<{ level: number; amount: number }> {
  const out: Array<{ level: number; amount: number }> = [];
  for (let level = Math.max(1, from) + 1; level <= Math.min(to, PROGRESSION.maxLevel); level++) {
    const amount = levelReward(level);
    if (amount > 0) out.push({ level, amount });
  }
  return out;
}

/** XP needed to go from `level` to `level + 1`. */
export function xpToNext(level: number): number {
  return PROGRESSION.base + PROGRESSION.step * (Math.max(1, level) - 1);
}

/** Total XP at which `level` starts (level 1 starts at 0). */
export function levelStartXp(level: number): number {
  const n = Math.max(1, Math.floor(level)) - 1;
  return n * PROGRESSION.base + (PROGRESSION.step * n * (n - 1)) / 2;
}

export function levelForXp(totalXp: number): number {
  let level = 1;
  while (level < PROGRESSION.maxLevel && levelStartXp(level + 1) <= totalXp) level++;
  return level;
}

export interface LevelProgress {
  level: number;
  /** XP earned inside the current level */
  into: number;
  /** XP the current level needs in total */
  need: number;
  /** 0..1 for the ring */
  ratio: number;
}

export function levelProgress(totalXp: number): LevelProgress {
  const level = levelForXp(totalXp);
  const into = Math.max(0, totalXp - levelStartXp(level));
  const need = xpToNext(level);
  return { level, into, need, ratio: level >= PROGRESSION.maxLevel ? 1 : Math.min(1, into / need) };
}

/** Next streak value after a settled round. */
export function nextStreak(streak: number, stake: number, payout: number): number {
  if (!(stake > 0)) return streak;
  if (payout > stake) return streak + 1;
  if (payout < stake) return 0;
  return streak;
}

/** Socket `progress:update` (to the player's own room) after every settled round. */
export interface ProgressEvent {
  level: number;
  /** total XP */
  xp: number;
  xpGained: number;
  streak: number;
  levelUp: boolean;
  /** tokens credited for the level(s) reached with this round (0 without a level-up) */
  reward: number;
}
